import { Avatar, FOCUS_RING, Icon, Spinner } from '@amelio/ui-library';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { participantAttributeKey } from '../participantAttributes';

import type { ParticipantAttributeGroup } from '../participantAttributes';
import type { User } from '@/types/directory/User';
import type { ObjectiveParticipantAttribute } from '@/types/objectives';
import type { ReactNode } from 'react';

import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

const OPTION_CLASS_NAME = `${FOCUS_RING} flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50`;

interface OptionGroupProps {
    heading: string;
    dataTestId: string;
    children: ReactNode;
}

/** A run of options under its type heading; the heading names the group and is never focusable. */
function OptionGroup({ heading, dataTestId, children }: OptionGroupProps) {
    const headingId = useId();

    return (
        <div
            role={'group'}
            aria-labelledby={headingId}
            data-testid={dataTestId}
        >
            <div
                id={headingId}
                className={'text-obj-muted px-4 pt-3 pb-1 text-[12px] font-semibold'}
            >
                {heading}
            </div>
            {children}
        </div>
    );
}

const LoadingRow = () => (
    <div className={'flex justify-center p-4'}>
        <Spinner size={'sm'} />
    </div>
);

interface ObjectiveEmployeeOptionListProps {
    employees: User[];
    areEmployeesLoading: boolean;
    attributeGroups: ParticipantAttributeGroup[];
    areAttributesLoading: boolean;
    /** True only once the company, or the teams within the reader's reach, could not be read, and
     * only where values are offered. */
    hasAttributeLoadError: boolean;
    onPickEmployee: (employeeId: string) => void;
    onPickAttribute: (attribute: ObjectiveParticipantAttribute) => void;
}

/** What the field offers: the people the search found, then the company values whose members take
 * part. Each source answers on its own, and nothing is called empty before all of them have. */
export default function ObjectiveEmployeeOptionList({
    employees,
    areEmployeesLoading,
    attributeGroups,
    areAttributesLoading,
    hasAttributeLoadError,
    onPickEmployee,
    onPickAttribute,
}: ObjectiveEmployeeOptionListProps) {
    const { t } = useTranslation();

    // "Nothing found" is only said once every source has answered.
    const hasNoResult =
        !areEmployeesLoading &&
        !areAttributesLoading &&
        !hasAttributeLoadError &&
        employees.length === 0 &&
        attributeGroups.length === 0;

    return (
        <>
            {areEmployeesLoading && <LoadingRow />}

            {!areEmployeesLoading && employees.length > 0 && (
                <OptionGroup
                    heading={t('objectives.form.participantPicker.groups.employees')}
                    dataTestId={'objective-employee-group-employees'}
                >
                    {employees.map((employee) => (
                        <button
                            key={employee.id}
                            type={'button'}
                            role={'option'}
                            aria-selected={false}
                            onClick={() => onPickEmployee(employee.id)}
                            className={OPTION_CLASS_NAME}
                            data-testid={`objective-employee-option-${employee.id}`}
                        >
                            <Avatar
                                src={getProfilePictureUrl(employee.profilePictureUrl)}
                                alt={getFullName(employee)}
                                initials={getInitials(employee)}
                                size={'xs'}
                            />
                            <span className={'text-[14px] font-medium text-gray-900'}>
                                {getFullName(employee)}
                            </span>
                        </button>
                    ))}
                </OptionGroup>
            )}

            {attributeGroups.map((group) => (
                <OptionGroup
                    key={group.key}
                    heading={group.heading}
                    dataTestId={`objective-employee-group-${group.key}`}
                >
                    {group.options.map((option) => (
                        <button
                            key={option.attribute.valueId}
                            type={'button'}
                            role={'option'}
                            aria-selected={false}
                            onClick={() => onPickAttribute(option.attribute)}
                            className={OPTION_CLASS_NAME}
                            data-testid={`objective-attribute-option-${participantAttributeKey(option.attribute)}`}
                        >
                            <Icon
                                name={group.icon}
                                size={'sm'}
                                colorClassName={'text-obj-muted'}
                            />
                            <span className={'text-[14px] font-medium text-gray-900'}>
                                {option.name}
                            </span>
                        </button>
                    ))}
                </OptionGroup>
            ))}

            {!areEmployeesLoading && areAttributesLoading && <LoadingRow />}

            {hasAttributeLoadError && (
                <p className={'text-obj-muted p-4 text-center text-[14px]'}>
                    {t('objectives.form.participantPicker.attributesLoadError')}
                </p>
            )}

            {hasNoResult && (
                <p
                    className={'p-4 text-center text-[14px] text-gray-500'}
                    data-testid={'objective-employee-no-results'}
                >
                    {t('objectives.form.noEmployeesFound')}
                </p>
            )}
        </>
    );
}
